<template>
  <div :class="{questions__save: safeFreeze}" class="questions">
    <h1 class="questions__header">{{ $t("Sign") }}</h1>
    <h2 v-show="!displayFrontend" class="questions__subheader questions__inputerrordescription">
      {{ $t("There is an error, not all tabs have data.") }}</h2>

    <div v-show="displayFrontend">
      <p>{{
          $t("In this step you confirm the entered settings, based on these settings the Dyflexis system will be set up. To avoid delays during the implementation and possible additional costs, we recommend that you to double check the settings below.")
        }}</p>

      <template v-if="allData.externalPayment === false && allData.billingRemarks === null
      && allData.accountIban === null && showBankAccountDetails === true">
        <p style="color:red;">{{$t("Not all mandatory payment details have been entered yet, please do this immediately.")}}</p>
      </template>

      <div id="signedSettings">
        <h2 class="questions__subheader questions__header bold">{{ $t("System") }}</h2>
        <p class="questions--noinputmargin">{{ $t("Desired system name") }}: app.planning.nu/{{ allData.systemUrl }}</p>
        <p class="questions--noinputmargin">{{ $t("Default system language") }}: {{ allData.systemLanguage }}</p>
        <p v-show="allData.systemSettingsRemarks" class="questions--noinputmargin">{{ $t("Remarks") }}:
          {{ allData.systemSettingsRemarks }}</p>

        <h2 class="questions__subheader questions__header bold">{{ $t("Company structure") }}</h2>
        <div v-if="showOldDepartmentOverview">
          <p class="questions--noinputmargin questions--slashNenter">{{
              $t("Department structure")
            }}:<br>{{ this.removeHtml(allData.systemBusinessUnits) }}</p>
          <p class="question--marginTop questions--slashNenter">{{
              $t("Department structure")
            }}:<br>{{ this.removeHtml(allData.systemDepartments) }}</p>
        </div>
        <div v-if="!showOldDepartmentOverview">
          <p>
            <template v-for="(unit, index) in allData.systemBusinessUnits">
              <template v-if="index>0"><br></template>
              - {{ unit.name }}: <br><i>{{ unit.structure }}</i><br>
            </template>
          </p>
        </div>
        <p class="questions--slashNenter">{{ $t("Secondment") }}: {{ this.trueFalseToNative(allData.systemDepartmentSecondment) }}</p>

        <h2 class="questions__subheader questions__header bold">{{ $t("Employee Management") }}</h2>
        <p class="questions--noinputmargin">{{ $t("Sort employees by") }}: {{ allData.employeePermissionSortNames }}</p>
        <br>
        <p class="questions--noinputmargin">{{ $t("General") }}: {{ labelSettings.general }}</p>
        <p class="questions--noinputmargin">{{ $t("Skills") }}: {{ labelSettings.skills }}</p>
        <p class="questions--noinputmargin">{{ $t("Training") }}: {{ labelSettings.education }}</p><br>

        <p class="questions--noinputmargin">{{ $t("See daily schedule") }}: {{
            this.trueFalseToNative(allData.employeePermissionDaySchedule)
          }}</p>
        <p class="questions--noinputmargin">{{ $t("View weekly schedule") }}: {{
            this.trueFalseToNative(allData.employeePermissionWeekSchedule)
          }}</p>
        <p class="questions--noinputmargin">{{ $t("See vacation schedule") }}:
          {{ this.trueFalseToNative(allData.employeePermissionHolidaySchedule)}}</p>
        <p class="questions--noinputmargin">{{ $t("Employees may see the above schedules from:") }}
          {{ this.trueFalseToNative(allData.employeePermissionViewSchedule)}}</p><br>

        <p class="questions--noinputmargin">{{ $t("Employees allowed to see end time of a shift") }}:
          {{ this.trueFalseToNative(allData.employeePermissionSeeEndTimeShift) }}</p>
        <p class="questions--noinputmargin">{{ $t("Employees can swap shifts") }}:
          {{ this.trueFalseToNative(allData.employeePermissionHandoverShift) }}</p>
        <p class="questions--noinputmargin">{{ $t("Employees can register hours for their themselves") }}:
          {{ this.trueFalseToNative(allData.employeePermissionRegisterHours) }}</p>
        <p class="questions--noinputmargin">{{ $t("Employees can clock in the app if they are on the schedule") }}:
          {{ this.trueFalseToNative(allData.employeePermissionClockInApp) }}</p>
        <p class="questions--noinputmargin">{{ $t("Employees can only clock-in if their phone is within 150 meters from the location") }}:
          {{ this.trueFalseToNative(allData.employeePermissionClockInApp) }}</p>
        <br>

        <p class="questions--noinputmargin">{{ $t("Vacation request period, in days") }}:
          {{ allData.employeePermissionHolidayDayAdvance }}</p>
        <p class="questions--noinputmargin">{{ $t("Unavailability request period, in days") }}:
          {{ allData.employeePermissionLeaveDayAdvance }}</p>
        <p v-show="allData.employeePermissionRemarks" class="questions--noinputmargin">{{ $t("Remarks") }}:
          {{ allData.employeePermissionRemarks }}</p>

        <h2 class="questions__subheader questions__header bold">{{ $t("Contracts") }}</h2>
        <p class="questions--noinputmargin">{{ $t("Contract types") }}:
          <template v-for="(contractType, index) in allData.contracts.contractTypes" v-if="contractType.active===true">
            <template v-if="index>0">, </template>{{contractType.displayName}}</template>
        </p>
        <p class="questions--noinputmargin">{{ $t("Contract Basis") }}:
          <template v-for="(contractType, index) in allData.contracts.contractTypes"
                    v-if="contractType.active===true && contractType.contractType">
            <template v-if="index>0">, </template>{{ contractType.displayName }}</template>
        </p>
        <p v-show="allData.contracts.multiplePayrollCompanies !== undefined && allData.contracts.multiplePayrollCompanies !== null"
           class="questions--noinputmargin">
          {{ $t("In case you work with several employment agencies: enter the names of the payroll agencies.") }}:
          {{ allData.contracts.multiplePayrollCompanies }}</p>
        <p class="questions--noinputmargin">{{ $t("Remuneration timeframe") }}:
          <template v-for="(contractType, index) in allData.contracts.contractTypes"
                    v-if="contractType.active===true">
            <template v-if="index>0">,</template>
            {{ contractType.displayName }}: {{ contractType.payPeriod }} <template v-if="contractType.startDayPayPeriod!==''">({{ contractType.startDayPayPeriod }})</template>
          </template>
        </p>
        <p class="questions--noinputmargin">{{ $t("Contractbase") }}:
          <template v-for="(contractType, index) in allData.contracts.contractTypes"
                    v-if="contractType.active===true && contractType.contractType">
            <template v-if="index>0">,</template>
            {{ contractType.displayName }}: {{ contractType.calculateBy }}
          </template>
        </p>
        <p class="questions--noinputmargin">{{ $t("How many hours is a full-time work week?").replace('?', '') }}:
          {{ allData.contracts.fulltimeHours }}</p>
        <p v-show="allData.contracts.fulltimeHoursOther !== null && allData.contracts.fulltimeHoursOther !== ''"
           class="questions--noinputmargin">
          {{
            $t("If you are using multiple full-time hours, please enter them below")
          }}:<br>{{ allData.contracts.fulltimeHoursOther }}</p>
        <p v-show="allData.contracts.jusDescription !== null" class="questions--noinputmargin">
          {{ $t("We use annual hours system") }}: {{ allData.contracts.jusDescription }}</p>
        <p v-show="allData.contracts.minMaxDescription !== null" class="questions--noinputmargin">
          {{ $t("Explanation min-max") }}:<br> {{ allData.contracts.minMaxDescription }}<br></p>
        <p v-show="allData.contracts.remarks !== null" class="questions--noinputmargin">{{ $t("Remarks") }}:
          {{ allData.contracts.remarks }}</p>

        <template v-if="allData.overtime.overtimePay !== null">
          <h2 class="questions__subheader questions__header bold">{{ $t("Overtime") }}</h2>
          <p class="questions--noinputmargin">{{ $t("How do you handle overtime?").replace('?', '') }}: {{
              allData.overtime.overtimePay
            }}</p>
          <p class="questions--noinputmargin">{{ $t("Other overtime rules") }}: {{
              allData.overtime.overtimePayNotes
            }}</p>
          <p v-show="allData.overtime.tftPayoutThreshold !== null" class="questions--noinputmargin">
            {{ $t("Pay out above how many hours?").replace('?', '') }} {{ allData.overtime.tftPayoutThreshold }}</p>
          <p v-show="allData.overtime.workingHoursActWarning === true" class="questions--noinputmargin">
            {{ $t("Yes, we want to see warnings when the working hours act is exceeded") }}</p>
          <p v-show="allData.overtime.remarks !== null" class="questions--noinputmargin">{{ $t("Remarks") }}:
            {{ allData.overtime.remarks }}</p>
        </template>

        <h2 class="questions__subheader questions__header bold">{{ $t("Allowances") }}</h2>
        <template v-for="(allowanceRule,index) in allData.contracts.allowanceRules" v-if="allowanceRule.name !== ''">
          <p class="questions--noinputmargin">
            <p v-show="index>1"></p>
            {{ $t("Allowance type") }}: {{ $t(allowanceRule.type) }}<br>
            {{ $t("Allowance name") }}: {{ allowanceRule.name }}<br>
            {{ $t("Percentage allowance") }}: {{ allowanceRule.surchargeRate }}<br>
            <template v-if="allowanceRule.period">Over de periode: {{ allowanceRule.period }}<br></template>
            <template v-if="allowanceRule.threshold">{{ $t("Valid from") }}: {{ allowanceRule.threshold }}<br>
            </template>
            <template v-if="allowanceRule.threshold==='thresholdHours'">{{ $t("From time to time") }}:
              {{ allowanceRule.thresholdHours }}<br></template>
            <template v-if="allowanceRule.days">{{ $t("Valid on days") }}:
              <template v-for="(days, index) in allowanceRule.days">
                <template v-if="index>0&&days!==null">, </template>{{ days }}</template>
              <br></template>
            <template v-if="allowanceRule.holidayActive">{{ $t("Valid on days") }}:
              <template v-for="(custom, index) in allowanceRule.holidayActive">
                <template v-for="(translation) in holidayOptions"><span v-show="translation.value==custom">{{ translation.text }}, </span></template>
              </template><br></template>
            <template v-if="allowanceRule.holidayCustom!=null">{{ $t("Manual date") }}:
              <template v-for="(custom, index) in allowanceRule.holidayCustom">
                <br> - {{ custom[0] }}, {{ $t("Repeat annually") }}: <span v-if="custom[1]">{{ $t("Yes") }}</span><span
                v-if="!custom[1]">{{ $t("No") }}</span>
              </template>
              <br></template>
            <template v-if="allowanceRule.type!=='overtime'">{{ $t("From time to time") }}: {{
                allowanceRule.startTime
              }} - {{ allowanceRule.endTime }}<br></template>
            <template v-if="allowanceRule.overtime">{{ $t("Entire shift") }}: {{ allowanceRule.overtime }}<br>
            </template>
            <span v-show="allowanceRule.calculateForHourTypes !== undefined">{{ $t("For hourtypes") }}:</span>
            <template v-if="allowanceRule.calculateForHourTypes !== undefined"
                      v-for="(custom, index) in allowanceRule.calculateForHourTypes">
              <template v-if="index>0">,</template> {{ $t(custom) }}</template>
            <span v-show="allowanceRule.calculateForHourTypes !== undefined"><br></span>
            {{ $t("Contract type") }}:
            <template v-for="(contractType, index) in allowanceRule.enabledContracts">
              <template v-if="index>0">,</template>
              {{ contractType }}</template>
            <br>
            {{ $t("Type of compensation") }}: {{ allowanceRule.compensation }}<br>
            {{ $t("Other conditions") }}: {{ allowanceRule.conditions }}
          </p>
        </template>
        <p class="questions--noinputmargin">{{ $t("Remarks") }}: {{ allData.contracts.allowanceRemarks }}</p>

        <h2 class="questions__subheader questions__header bold">{{ $t("Leave") }}</h2>
        <p class="questions--noinputmargin">{{ $t("Contract type") }}:
          <template v-for="(contractType, index) in allData.contracts.contractTypes"
                    v-if="contractType.active===true&&contractType.saveLeaveAmount===true">
            <template v-if="index>0">,</template>
            {{ contractType.displayName }}</template>
        </p>
        <p class="questions--noinputmargin">{{
            $t("How many hours of leave does an employee with a full-time work week ({ftHours} hours) get per year", {ftHours: allData.contracts.fulltimeHours})
          }}: {{ allData.contracts.holidayHours }}</p>
        <p class="questions--noinputmargin">{{ $t("What is the vacation accrual percentage?").replace('?', '') }}:
          {{ allData.contracts.holidayPercentage }}%</p>
        <p class="questions--noinputmargin">{{
            $t("Do you also want to track vacation due dates based on statutory and excess hours based on the predicted annual balance?").replace('?', '')}}:
          {{ this.trueFalseToNative(allData.contracts.keepAnnualBalanceLawfull) }}</p>
        <p v-show="allData.contracts.amountShortTimeWorkingHours !== null" class="questions--noinputmargin">
          {{ $t("How many reduced working hours do full-time employees get per year?").replace('?', '') }}:
          {{ allData.contracts.amountShortTimeWorkingHours }}</p>
        <p v-show="allData.contracts.amountShortTimeWorkingHours !== null" class="questions--noinputmargin">
          {{ $t("This applies to the following contract types:") }}
          <template v-for="(contractType, index) in allData.contracts.contractTypes"
                    v-if="contractType.active===true&&contractType.shortTimeWorkingEnabled">
            {{ contractType.displayName }}<template v-if="index>0">,</template></template>
        </p>
        <p v-show="allData.contracts.leaveRemarks !== null" class="questions--noinputmargin">{{ $t("Remarks") }}:
          {{ allData.contracts.leaveRemarks }}</p>

        <h2 class="questions__subheader questions__header bold">{{ $t("Hourtypes") }}</h2>
        <p class="questions--noinputmargin">
          <template v-if="allData.hourTypes.workedHours === true">{{ $t("Hours worked") }},</template>
          <template v-if="allData.hourTypes.holidayLeave === true">{{ $t("Holiday Hours") }},</template>
          <template v-if="allData.hourTypes.workTimeReduction === true">{{ $t("Worktime reduction hours") }},</template>
          <template v-if="allData.hourTypes.sicknessAbsence === true">{{ $t("Sickness leave") }},</template>
          <template v-if="allData.hourTypes.payedLeave === true">{{ $t("Paid leave") }},</template>
          <template v-if="allData.hourTypes.unpaidLeave === true">{{ $t("Unpaid leave") }},</template>
          <template v-if="allData.hourTypes.specialLeave === true">{{ $t("Special leave") }},</template>
          <template v-if="allData.hourTypes.maternityLeave === true">{{ $t("Pregnancy leave") }},</template>
          <template v-if="allData.hourTypes.feastdayLeave === true">{{ $t("Public holiday leave") }},</template>
          <template v-if="allData.hourTypes.birthLeave === true">{{ $t("Birth leave") }},</template>
          <template v-if="allData.hourTypes.birthAdditionalLeave === true">{{ $t("Additional birth leave") }},</template>
          <template v-if="allData.hourTypes.careLeave === true">{{ $t("Care leave") }},</template>
          <template v-if="allData.hourTypes.emergencyLeave === true">{{ $t("Emergency leave") }},</template>
          <template v-if="allData.hourTypes.shortTermLeave === true">{{ $t("Short-term leave") }},</template>
          <template v-if="allData.hourTypes.doctorLeave === true">{{ $t("Doctor's leave") }},</template>
          <template v-if="allData.hourTypes.parentalLeave === true">{{ $t("Parental leave") }},
          </template>
          <template v-if="allData.hourTypes.unpaidParentalLeave === true">{{ $t("Unpaid parental leave") }},
          </template>
          <template v-if="allData.hourTypes.educationalLeave === true">{{ $t("Educational leave") }},
          </template>
          <template v-if="allData.hourTypes.waitdayAbsence === true">{{ $t("Sickness waiting day") }},
          </template>
        </p>
        <p class="questions--noinputmargin">
          {{ $t("Yes, we have additional hourly types") }}: {{ allData.hourTypes.additionalHours }}</p>
        <p class="questions--noinputmargin">
          {{ $t("Yes, we use standby, sleep or consignment shifts") }}: {{ allData.hourTypes.consignmentShift }}</p>
        <p class="questions--noinputmargin">{{ $t("Automatic distance calculation") }}:
          {{ allData.hourTypes.automaticDistanceCalculation }}</p>

        <p>{{$t("Which reimbursements do you have?").replace('?', '') }}:
          <template v-if="allData.hourTypes.mealCompensation === true">{{ $t("Meal") }}, </template>
          <template v-if="allData.hourTypes.distanceCompensation === true">{{ $t("Distance") }}, </template>
          <template v-if="allData.hourTypes.workFromHomeCompensation === true">{{ $t("Working from home") }}, </template>
          <template v-if="allData.hourTypes.otherCompensations !== null">{{ allData.hourTypes.otherCompensations }}</template>
        </p>

        <h2 class="questions__subheader questions__header bold">{{ $t("Break rules") }}</h2>
        <template v-for="(breakRule) in allData.breakRules.workedHoursBreak" v-if="breakRule.breakTime !== null && breakRule.breakTime !== ''"
                  class="questions--noinputmargin">{{ breakRule.group }}: {{ $t("In case") }} {{ breakRule.hoursWorked }} {{
            $t("hours worked")
          }}, {{ breakRule.breakLength }} {{ $t("minutes break") }}<br></template>
        <template v-for="(breakRule) in allData.breakRules.timeBreak" v-if="breakRule.breakTime !== null && breakRule.breakTime !== ''"
                  class="questions--noinputmargin">{{ breakRule.group }}: {{ $t("If working at this time:").replace(':', '') }} {{ breakRule.breakTime }},
          {{ breakRule.breakLength }} {{ $t("minutes break") }}<br></template>
        <p v-show="allData.breakRulesRemarks" class="questions--noinputmargin">{{ $t("Remarks") }}:
          {{ allData.breakRulesRemarks }}</p>

        <template v-if="allData.clockRules !== null">
          <h2 class="questions__subheader questions__header">{{ $t("Rounding rules") }}</h2>

          <template v-for="(workDayRule, index) in allData.clockRules.workDayRules">
            <p class="questions--noinputmargin bold">{{$t("Round total shift times")}}: {{ workDayRule.name }}</p>
            <p class="questions--noinputmargin">{{ $t("Round clock times on") }}:
              {{ workDayRule.roundMinutes }} {{ $t("min.") }}</p>
            <p class="questions--noinputmargin">{{ $t("Ignore clocked time") }}
              {{ workDayRule.ignoreEarlyClockInMinutes }} {{ $t("min. before scheduled start time and") }}
              {{ workDayRule.ignoreOverWorkMinutes }} {{ $t("min. after scheduled end time.") }}</p>
            <p class="questions--noinputmargin"> {{
                $t('Employees may be {ignoreLateClockInMinutes} late, and {ignoreEarlyClockOutMinutes} minutes early without affecting the clock times.', {
                  ignoreLateClockInMinutes: workDayRule.ignoreLateClockInMinutes,
                  ignoreEarlyClockOutMinutes: workDayRule.ignoreEarlyClockOutMinutes
                })
              }}</p><br>
          </template>

          <template v-for="(workDayRule, index) in allData.clockRules.clockItemRules">
            <p class="questions--noinputmargin bold">{{$t("Round clock times")}}: {{ workDayRule.name }}</p>
            <p class="questions--noinputmargin">{{ $t("Round clock times on") }}:
              {{ workDayRule.roundMinutes }} {{ $t("min.") }}</p>
            <p class="questions--noinputmargin">{{ $t("Ignore clocked time") }}
              {{ workDayRule.ignoreEarlyClockInMinutes }} {{ $t("min. before scheduled start time and") }}
              {{ workDayRule.ignoreOverWorkMinutes }} {{ $t("min. after scheduled end time.") }}</p>
            <p class="questions--noinputmargin"> {{
                $t('Employees may be {ignoreLateClockInMinutes} late, and {ignoreEarlyClockOutMinutes} minutes early without affecting the clock times.', {
                  ignoreLateClockInMinutes: workDayRule.ignoreLateClockInMinutes,
                  ignoreEarlyClockOutMinutes: workDayRule.ignoreEarlyClockOutMinutes
                })
              }}</p><br>
          </template>

          <p class="questions--noinputmargin">{{$t("Clock breaks")}}:
            <template v-if="allData.clockRules.clockBreaks === 'no'">{{ $t("No, breaks are not clocked") }}</template>
            <template v-if="allData.clockRules.clockBreaks === 'indicator'">{{ $t("Indicative: the break rule is leading, but the actual timed break is shown as an indication") }}</template>
            <template v-if="allData.clockRules.clockBreaks === 'clockedLeading'">{{ $t("Clocked break is leading: the clocked break is deducted from the hours worked") }}</template>
            <template v-if="allData.clockRules.clockBreaks === 'ruleElseClocked'">{{ $t("Clocking less break than the break rule: round up to the break rule, with too much break time clocks deduct from the hours worked") }}</template>
          </p>

          <template v-if="allData.clockRules.costCenterSwitching === true">
            <p>{{ $t("Should it be possible for employees to switch between different cost centers during their shift?").replace('.', '') }}: {{ $t("Yes, enable cost center switching") }}</p>
          </template>

          <p v-show="allData.clockRulesRemarks" class="questions--noinputmargin">{{ $t("Remarks") }}:
            {{ allData.clockRulesRemarks }}</p>
        </template>
      </div>

      <h1 class="question--marginTop questions__header bold">{{ $t("Confirm setup") }}</h1>
      <p>{{ $t("We recommend that you verify that all involved parties in your organization agree with the chosen setup. These include the HR department, operational managers, the CFO and finance department. Changes passed on after confirmation will result in additional work, possibly delays") }}
        <b> {{ $t("and additional costs.") }}</b></p>
      <h2 class="questions__subheader">{{ $t("Your full name") }}</h2>
      <input
        v-model="Sign.signedName"
        :disabled="readOnly"
        class="form-control"
        type="text"
      />

      <h2 class="questions__subheader">{{ $t("Your e-mail, receiving confirmation of the settings") }}</h2>
      <input
        v-model="Sign.signedEmail"
        :disabled="readOnly"
        class="form-control"
        type="text"
      />

      <input :disabled="readOnly" :value="$t('Setup Dyflexis environment with the settings above')" type="button"
             @click="handleSave"/>
      <p v-show="showSaveStatus" class="questions--savesuccess">{{ $t("Saved successfully!") }}</p>
    </div>
  </div>
</template>

<script>
import SystemRequestService from "@/SystemRequestService";
import GlobalFunctions from "@/GlobalFunctions";

export default {
  name: "Sign",
  data() {
    return {
      displayFrontend: true,
      safeFreeze: false,
      showSaveStatus: false,
      isSigned: false,
      showBankAccountDetails: true,
      showOldDepartmentOverview: true,//make it backwards compatible,
      allData: {
        contracts: {
          fulltimeHours: 40
        },
        overtime: {
          overtimePay: null
        },
        clockRules: null
      },
      roundRules: null,
      labelSettings: [{
        general: null,
        skills: null,
        education: null,
      }],
      contracts: null,
      Sign: {
        signedName: null,
        signedEmail: null,
        signedRaw: null,
      },
      holidayOptions: [
        {value: 'newYear', text: this.$t("New year's day")},
        {value: 'easterMonday', text: this.$t("Easter monday")},
        {value: 'liberationDay', text: this.$t("Liberation day")},
        {value: 'ascensionDay', text: this.$t("Ascension day")},
        {value: 'independenceDay', text: this.$t("Independence day")},
        {value: '1pentecost', text: this.$t("Pentecost")},
        {value: '2pentecost', text: this.$t("Whit monday")},
        {value: '1christmas', text: this.$t("Christmas day")},
        {value: '2christmas', text: this.$t("Boxing day")},
      ],
    };
  },
  computed: {
    readOnly() {
      return window.readOnly;
    }
  },
  async beforeMount() {
    const result = await SystemRequestService.getProject(this.$route.params.hashedId);

    if (!result) {
      alert(this.$t("Error while obtaining your details. Please try again later or contact your Dyflexis consultant."));
      return;
    }

    GlobalFunctions.setReadOnly(result.isSigned);

    for (let [key, value] of Object.entries(result)) {
      if (value === 'All') {
        this.allData[key] = this.$t("Everybody");
        continue;
      }

      if (value === 'No, read only') {
        this.allData[key] = this.$t("No, readonly");
        continue;
      }

      if (value === 'See own department') {
        this.allData[key] = this.$t("Only their own department(s)");
        continue;
      }

      if (value === 'No, hide tab') {
        this.allData[key] = this.$t("No, hide settings");
        continue;
      }

      if (value === 'See full company') {
        this.allData[key] = this.$t("The entire establishment");
        continue;
      }

      if (value === 'Employee number') {
        this.allData[key] = this.$t("Employee number");
        continue;
      }

      if (value === 'First names') {
        this.allData[key] = this.$t("Firstname");
        continue;
      }

      if (value === 'Last names') {
        this.allData[key] = this.$t("Lastname");
        continue;
      }

      if (value === '4-weekly') {
        this.allData[key] = this.$t("4-weekly");
        continue;
      }

      if (value === 'weekly') {
        this.allData[key] = this.$t("Weekly");
        continue;
      }

      if (value === 'monthly') {
        this.allData[key] = this.$t("Monthly");
        continue;
      }

      if (value === 'firstDayMonth') {
        this.allData[key] = this.$t("first of the month");
        continue;
      }

      if (value === 'montlyAverage') {
        this.allData[key] = this.$t("Average");
        continue;
      }

      if (value === 'workableDays') {
        this.allData[key] = this.$t("Workable days:");
        continue;
      }

      this.allData[key] = value;
    }

    if ("accountIban" in result) {
      this.showBankAccountDetails = true;
    } else {
      this.showBankAccountDetails = false;
    }

    this.labelSettings = this.allData.labelSettings[0];
    if (this.allData.clockRules.length !== null) {
      this.allData.clockRules = this.getFormatted(this.allData.clockRules);
    }

    this.allData.contracts = this.getFormatted(this.allData.contracts);
    this.allData.overtime = this.getFormatted(this.allData.overtime);
    this.allData.hourTypes = this.getFormatted(this.allData.hourTypes);
    this.allData.breakRules = this.getFormatted(this.allData.breakRules);

    // group allowance types
    this.allData.contracts.allowanceRules.sort(function (a, b) {
      if (a.type < b.type) return -1;
      if (a.type > b.type) return 1;
      return 0;
    });

    if (this.allData.clockRules != null) {
      if (this.allData.clockRules.roundBasedOn === 'workDay') {
        this.allData.clockRules.roundBasedOn = this.$t("Round total shift times on");
      }

      if (this.allData.clockRules.roundBasedOn === 'clockItem') {
        this.allData.clockRules.roundBasedOn = this.$t("Round clock times on");
      }

      if (this.allData.overtime.overtimePay === 'plusHoursTfT') {
        this.allData.overtime.overtimePay = this.$t("All plus hours to TFT balance");
      }

      if (this.allData.overtime.overtimePay === 'payOvertimeTfTPlusHours') {
        this.allData.overtime.overtimePay = this.$t("Pay plus hours, transfer overtime hours to TFT balance");
      }

      if (this.allData.overtime.overtimePay === 'payOvertimePayPlusHours') {
        this.allData.overtime.overtimePay = this.$t("Payout both plus and overtime hours");
      }
    }

    // Makes it backwards compatible. This can be removed after october 2022
    if (this.allData.systemBusinessUnits !== null && Array.isArray(this.getFormatted(this.allData.systemBusinessUnits))) {
      this.allData.systemBusinessUnits = this.getFormatted(this.allData.systemBusinessUnits);
      this.showOldDepartmentOverview = false;
    }

    this.allData.systemBusinessUnits.forEach((unit) => {
      unit.structure = unit.structure.replaceAll('&gt;', '>');
    });

    this.displayFrontend = true;
  },
  methods: {
    async handleSave() {
      if (this.Sign.signedEmail === '' || this.Sign.signedEmail === null || this.Sign.signedName === '' || this.Sign.signedName === null) {
        alert(this.$t("Name and email are required fields"));
        return;
      }

      this.safeFreeze = true;
      this.Sign.signedRaw = document.getElementById('signedSettings').outerHTML;
      this.Sign.signedRaw = this.Sign.signedRaw.replace(/(?:\r\n|\r|\n)/g, '<br>');

      const result = await SystemRequestService.updateProject(this.Sign, this.$route.params.hashedId);
      if (!result || result.status !== 'success') {
        alert(this.$t("Error while updating, please send a screenshot of the fields on this page to your Dyflexis consultant!"));
        return;
      }

      GlobalFunctions.setReadOnly(result.isSigned);
      localStorage.setItem('startingObject', JSON.stringify(this.Sign));
      this.safeFreeze = false;
      this.showSaveStatus = true;
      setTimeout(() => {
        this.showSaveStatus = false;
        this.$router.push(`/projectteam/${this.$route.params.hashedId}`);
      }, 3000);
    },
    trueFalseToNative(text) {
      if (text === true) {
        return this.$t("Yes");
      }

      if (text === false) {
        return this.$t("No");
      }

      return text;
    },
    removeHtml(text) {
      if (text == null) {
        return text;
      }

      let tmp = document.createElement("DIV");
      tmp.innerHTML = text.replaceAll("</p><p>", "\n").replaceAll("<br>", "\n");
      return tmp.textContent || tmp.innerText || "";
    },
    getFormatted(json) {
      try {
        return JSON.parse(json.replaceAll("&quot;", '"'));
      } catch (error) {
        return json;
      }
    }
  },
};
</script>
